import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { login } from '../Services';
import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  useEffect(() => {
    // Repli de l'inscription : quand la connexion automatique n'aboutit pas,
    // SignUpScreen renvoie ici avec l'adresse déjà saisie, pour ne pas la redemander.
    const emailPreRempli = (location.state as { emailPreRempli?: string } | null)?.emailPreRempli;
    if (emailPreRempli) {
      setEmail(emailPreRempli);
      return;
    }

    const rememberedEmail = localStorage.getItem('rememberedEmail');
    const rememberedPassword = localStorage.getItem('rememberedPassword');
    if (rememberedEmail && rememberedPassword) {
      setEmail(rememberedEmail);
      setPassword(rememberedPassword);
      setRememberMe(true);
    }
  }, [location.state]);

  const handleLogin = async () => {
    setLoginError(false);
    if (!email.trim() || !password.trim()) {
      setLoginError(true);
      return;
    }

    try {
      const user = await login(email, password);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('mail', email);

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberedPassword', password);
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
      }

      setEmail('');
      setPassword('');
      navigate('/retour');
    } catch (error) {
      setLoginError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <Box
      sx={{
        backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      {/* Desktop: split layout */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: isDesktop ? 900 : 420,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          backgroundColor: 'white',
        }}
      >
        {/* Left panel - branding (desktop only) */}
        {isDesktop && (
          <Box
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.06)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -40,
                left: -40,
                width: 160,
                height: 160,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.04)',
              }}
            />

            <Box
              component="img"
              src="/logoblanc.png"
              alt="Visamonde"
              sx={{ width: 220, height: 'auto', mb: 4, objectFit: 'contain' }}
            />
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 700,
                textAlign: 'center',
                mb: 1.5,
                lineHeight: 1.3,
              }}
            >
              Vos visas en toute simplicité
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.75)',
                textAlign: 'center',
                maxWidth: 280,
                lineHeight: 1.6,
              }}
            >
              Gérez vos demandes de visa, suivez leur avancement et téléchargez vos documents depuis une seule application.
            </Typography>
          </Box>
        )}

        {/* Right panel - login form */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3.5, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {/* Logo (mobile only) */}
          {!isDesktop && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                component="img"
                src="/logo.png"
                alt="Logo"
                sx={{ width: 200, height: 'auto', mx: 'auto', objectFit: 'contain' }}
              />
            </Box>
          )}

          {/* Title (desktop) */}
          {isDesktop && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
                Connexion
              </Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                Accédez à votre espace Visamonde
              </Typography>
            </Box>
          )}

          {/* Email */}
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#f0f1f2' },
                '&.Mui-focused': { backgroundColor: 'white' },
              },
            }}
          />

          {/* Password */}
          <TextField
            label="Mot de passe"
            variant="outlined"
            fullWidth
            margin="normal"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#f0f1f2' },
                '&.Mui-focused': { backgroundColor: 'white' },
              },
            }}
          />

          {/* Remember me */}
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                sx={{
                  color: '#ccc',
                  '&.Mui-checked': { color: '#2e7d32' },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#666' }}>
                Se souvenir de moi
              </Typography>
            }
            sx={{ mt: 0.5, ml: 0 }}
          />

          {/* Login button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleLogin}
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
              boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1b5e20 0%, #145218 100%)',
                boxShadow: '0 6px 20px rgba(46,125,50,0.4)',
              },
            }}
          >
            Connexion
          </Button>

          {/* Error */}
          {loginError && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: '#ffebee',
                border: '1px solid #ffcdd2',
              }}
            >
              <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 500, textAlign: 'center' }}>
                Utilisateur ou mot de passe erroné
              </Typography>
            </Box>
          )}

          {/* Links */}
          <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/signup')}
              sx={{
                color: '#2e7d32',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Créer un compte
            </Link>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/forgot-password')}
              sx={{
                color: '#888',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline', color: '#666' },
              }}
            >
              Mot de passe oublié
            </Link>
          </Box>

          {/* Version */}
          <Typography
            variant="caption"
            sx={{ mt: 3, textAlign: 'center', display: 'block', color: '#ccc' }}
          >
            v1.0.13
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginScreen;
